// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import MatiereService from '../../services/MatiereService';

const MatieresListItems = () => {
	const [originalMatieres, setOriginalMatieres] = useState([]);
	const [matieres, setMatieres] = useState([]);

	const SECTION_CHOICES = [
		{ value: 'en', label: 'Angolophone' },
		{ value: 'fr', label: 'Francophone' }
	];

	useEffect(() => {
		Promise.all([MatiereService.getAllMatieres()]).then(([matieresData]) => {
			setOriginalMatieres(matieresData);
			setMatieres(matieresData);
		});
	}, []);

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newMatieresList = originalMatieres.filter((matiere) => {
				return matiere.section === filterTerm;
			});
			setMatieres(newMatieresList);
		} else {
			setMatieres(originalMatieres);
		}
	};

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'nom' && value) {
				return value.toUpperCase();
			} else {
				return value;
			}
		}
		return value;
	};

	const getInitials = (matiere) => {
		const nom = matiere.nom ? matiere.nom.charAt(0).toUpperCase() : '';
		return `${nom} `;
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'nom',
				Header: 'Nom',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					return (
						<div className="d-flex align-items-center">
							<Avatar
								type="initial"
								size="md"
								name={initials}
								className="rounded-circle"
								alt={initials}
							/>
							{value ? (
								<Link to={`/pedagogie/matieres/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'nom')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'points',
				Header: 'Points',
				Cell: ({ row }) => {
					const section = row.original.section;

					if (section === 'fr') {
						return (
							<div>
								<div>CE2: {formatValue(row.original.points_16, 'points')}</div>
								<div>CM1: {formatValue(row.original.points_17, 'points')}</div>
								<div>CM2: {formatValue(row.original.points_18, 'points')}</div>
							</div>
						);
					} else if (section === 'en') {
						return (
							<div>
								<div>
									Class Four: {formatValue(row.original.points_10, 'points')}
								</div>
								<div>
									Class Five: {formatValue(row.original.points_11, 'points')}
								</div>
								<div>
									Class Six: {formatValue(row.original.points_12, 'points')}
								</div>
							</div>
						);
					} else {
						return <div>Section non spécifiée</div>;
					}
				}
			},

			{
				accessor: 'section',
				Header: 'Section',
				Cell: ({ value }) => <div>{value}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return matieres.map((matiere) => ({
			...matiere
		}));
	}, [matieres]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={3} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher une Matiere"
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="table text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default MatieresListItems;
